import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FuseConfigService } from '@fuse/services/config.service';
import { fuseConfig } from 'app/fuse-config';

/**
 * Fotos del album Building servidas por CloudFront en vez de por el API.
 *
 * El navegador no puede mandarle cookies a cloudfront.net, asi que las fotos se
 * piden a una ruta del MISMO origen (`/cdn/Building/...`) que nginx reenvia a
 * CloudFront. Las cookies firmadas las entrega el API y se guardan aqui
 * limitadas a esa ruta.
 *
 * Todo es opcional: mientras no haya cookies vigentes, o si el servidor todavia
 * no tiene la ruta `/cdn/`, `urlFor` devuelve null y quien llama sigue usando
 * `/shared/image` como siempre. Nada se rompe si el proxy no esta desplegado.
 */

// Para pasar a un dominio propio de CloudFront basta con cambiar esta base.
const CDN_BASE = '/cdn';
const BUILDING_ALBUM = 'Building';
const BUILDING_ALBUM_TYPE = 1;

const COOKIE_NAMES = ['CloudFront-Policy', 'CloudFront-Signature', 'CloudFront-Key-Pair-Id'];

// Las cookies se dejan de usar un rato antes de que venzan, y se renuevan antes.
const EXPIRY_MARGIN_MS = 5 * 60 * 1000;
// Si el API o el proxy no responden, no se insiste antes de este tiempo.
const RETRY_MS = 10 * 60 * 1000;

interface SignedCookiesResponse {
    cookies: { [name: string]: string };
    // Segundos de validez
    timeCookie: number;
}

interface BuildingPhoto {
    folder: string;
    img: string;
}

@Injectable({
    providedIn: 'root'
})
export class CdnPhotoService {
    // Hasta cuando sirven las cookies guardadas (epoch ms, ya con el margen).
    private validUntil = 0;
    // El servidor tiene la ruta /cdn/ y llega a CloudFront.
    private proxyAvailable = false;
    private preparing = false;
    private retryAfter = 0;
    // Sesion a la que pertenecen las cookies: si cambia, se descartan.
    private token: string | null = null;
    private building: string | null = null;
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;
    // El endpoint de cookies es solo del rol security.
    private isSecurity = false;

    constructor(private http: HttpClient, fuseConfigService: FuseConfigService) {
        // Los roles viven en la config en vivo del servicio, no en el objeto
        // `fuseConfig` importado, que solo trae los valores iniciales.
        fuseConfigService.config.subscribe(config => {
            this.isSecurity = !!config?.isSecurity;
        });
    }

    /**
     * Toda foto del album Building se arma igual: edificio + identificador.
     * Cada endpoint los entrega a su manera y aqui se extraen:
     *   - `urlFor`: el parametro hex que hoy se manda a /shared/image (guests).
     *   - `urlForPath`: el path `/security/building/<b>/photo/<id>` (residentes)
     *     o la URL `.../Building/<b>/<id>` (owner).
     * Devuelven null si todavia no se puede usar el CDN; en ese caso se deja
     * preparando en segundo plano para las siguientes fotos.
     */
    urlFor(img: string, building?: string | null): string | null {
        return this.photoUrl(this.decode(img), building);
    }

    urlForPath(path: string | null | undefined, building?: string | null): string | null {
        return this.photoUrl(this.parsePath(path), building);
    }

    /** True si las fotos ya pueden salir por el CDN (y no hace falta el base64). */
    isReady(building?: string | null): boolean {
        return this.ready(building || null);
    }

    private photoUrl(photo: BuildingPhoto | null, building?: string | null): string | null {
        if (!photo) {
            return null;
        }
        return this.ready(building || photo.folder) ? this.cdnUrl(photo) : null;
    }

    /**
     * Pide las cookies por adelantado, al elegir el gate, para que la primera
     * pantalla con fotos ya pueda usar el CDN.
     */
    warmUp(building: string | null | undefined): void {
        if (building) {
            this.ready(building);
        }
    }

    /** True si ya se puede usar el CDN; si no, lo deja preparando. */
    private ready(building: string | null): boolean {
        if (!this.isSecurity) {
            return false;
        }

        const token = this.currentToken();
        if (!token) {
            this.clear();
            return false;
        }
        if (token !== this.token) {
            this.clear();
            this.token = token;
        }

        if (this.proxyAvailable && Date.now() < this.validUntil) {
            return true;
        }

        if (building) {
            this.prepare(building);
        }
        return false;
    }

    /**
     * Parametro hex equivalente a una URL de `urlFor`, para poder volver a
     * /shared/image si la descarga por el CDN falla. Null si no es una URL del CDN.
     */
    legacyImgFor(url: string): string | null {
        const prefix = `${CDN_BASE}/${BUILDING_ALBUM}/`;
        if (typeof url !== 'string' || !url.startsWith(prefix)) {
            return null;
        }
        const parts = url.slice(prefix.length).split('/');
        if (parts.length !== 2 || !parts[0] || !parts[1]) {
            return null;
        }
        try {
            const json = JSON.stringify({
                type: BUILDING_ALBUM_TYPE,
                folder: decodeURIComponent(parts[0]),
                img: decodeURIComponent(parts[1])
            });
            return Array.from(new TextEncoder().encode(json))
                .map(byte => byte.toString(16).padStart(2, '0'))
                .join('');
        } catch {
            return null;
        }
    }

    /** Borra las cookies y el estado. Se llama al cerrar sesion. */
    clear(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        if (this.validUntil > 0) {
            COOKIE_NAMES.forEach(name => this.writeCookie(name, '', 0));
        }
        this.validUntil = 0;
        this.proxyAvailable = false;
        this.retryAfter = 0;
        this.token = null;
        this.building = null;
    }

    private prepare(building: string): void {
        if (this.preparing || Date.now() < this.retryAfter) {
            return;
        }
        this.preparing = true;
        this.building = building;
        const token = this.token;

        this.http
            .get<SignedCookiesResponse>(`${fuseConfig.url}/security/getSignedCookies?building=${building}`)
            .subscribe({
                next: async data => {
                    // La sesion cambio mientras llegaba la respuesta.
                    if (token !== this.token) {
                        this.preparing = false;
                        return;
                    }
                    const seconds = Number(data?.timeCookie);
                    const complete = COOKIE_NAMES.every(name => !!data?.cookies?.[name]);
                    if (!complete || !(seconds > 0)) {
                        this.failed();
                        return;
                    }

                    COOKIE_NAMES.forEach(name => this.writeCookie(name, data.cookies[name], seconds));
                    const lifetime = seconds * 1000;
                    this.validUntil = Date.now() + lifetime - EXPIRY_MARGIN_MS;
                    this.scheduleRefresh(lifetime - 2 * EXPIRY_MARGIN_MS);

                    if (!this.proxyAvailable) {
                        const available = await this.probe(building);
                        if (token !== this.token) {
                            this.preparing = false;
                            return;
                        }
                        this.proxyAvailable = available;
                        if (!available) {
                            this.retryAfter = Date.now() + RETRY_MS;
                        }
                    }
                    this.preparing = false;
                },
                error: () => this.failed()
            });
    }

    private failed(): void {
        this.preparing = false;
        this.retryAfter = Date.now() + RETRY_MS;
    }

    private scheduleRefresh(delay: number): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
        this.refreshTimer = setTimeout(() => {
            this.refreshTimer = null;
            if (this.building && this.token && this.token === this.currentToken() && this.isSecurity) {
                this.prepare(this.building);
            }
        }, Math.max(delay, 60 * 1000));
    }

    /**
     * Comprueba que `/cdn/` llega de verdad a CloudFront. Sin el bloque de
     * nginx esa ruta la contesta la propia web (o un 404) y no trae la
     * cabecera que CloudFront pone en todas sus respuestas, errores incluidos.
     */
    private async probe(building: string): Promise<boolean> {
        try {
            const response = await fetch(
                `${CDN_BASE}/${BUILDING_ALBUM}/${encodeURIComponent(building)}/cdn-probe`,
                { method: 'HEAD', credentials: 'same-origin', cache: 'no-store' }
            );
            return response.headers.has('x-amz-cf-id');
        } catch {
            return false;
        }
    }

    private cdnUrl(photo: BuildingPhoto): string {
        return `${CDN_BASE}/${BUILDING_ALBUM}/${encodeURIComponent(photo.folder)}/${encodeURIComponent(photo.img)}`;
    }

    /** Edificio e identificador de un path del proxy o de una URL del album. */
    private parsePath(path: string | null | undefined): BuildingPhoto | null {
        if (typeof path !== 'string') {
            return null;
        }
        const match =
            /^\/security\/building\/([^/?#]+)\/photo\/([^/?#]+)$/.exec(path) ||
            /^(?:https?:\/\/[^/]+)?\/Building\/([^/?#]+)\/([^/?#]+)$/.exec(path);
        if (!match) {
            return null;
        }
        try {
            return { folder: decodeURIComponent(match[1]), img: decodeURIComponent(match[2]) };
        } catch {
            return null;
        }
    }

    /** `{ type, folder, img }` codificado en hex por el API. */
    private decode(img: string): BuildingPhoto | null {
        if (typeof img !== 'string' || img.length === 0 || img.length % 2 !== 0 || /[^0-9a-f]/i.test(img)) {
            return null;
        }
        try {
            const bytes = new Uint8Array(img.length / 2);
            for (let i = 0; i < bytes.length; i++) {
                bytes[i] = parseInt(img.substr(i * 2, 2), 16);
            }
            const decoded = JSON.parse(new TextDecoder().decode(bytes));
            if (!decoded || Number(decoded.type) !== BUILDING_ALBUM_TYPE || !decoded.folder || !decoded.img) {
                return null;
            }
            return { folder: String(decoded.folder), img: String(decoded.img) };
        } catch {
            return null;
        }
    }

    private writeCookie(name: string, value: string, maxAgeSeconds: number): void {
        // Limitadas a /cdn/: no viajan en ninguna otra peticion.
        const secure = location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${name}=${value}; path=${CDN_BASE}/; max-age=${maxAgeSeconds}; SameSite=Strict${secure}`;
    }

    private currentToken(): string | null {
        try {
            return localStorage.getItem('token') || sessionStorage.getItem('token');
        } catch {
            return null;
        }
    }
}
